import { SupportedContentItemTypes } from "./SchoolFeedPostManager";
import { Dispatch, ReactElement, SetStateAction, useMemo } from "react";
import styles from "./styles/ContentItemSelect.module.scss";
import FlatIcon from "../../utilities/FlatIcon";
import { useGridType } from "../../../hooks/useGridType";
import { GridTypes } from "../../../utils/SchoolBlocksUtilities";
import { useStores } from "../../../stores/useStores";
import { isSchoolBlocksApp } from "../../../utils/SchoolBlocksUtilities";

function TopicIcon() {
  return (
    <svg
      version="1.0"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 70.000000 70.000000"
      preserveAspectRatio="xMidYMid meet"
    >
      <g
        transform="translate(0.000000,70.000000) scale(0.050000,-0.050000)"
        fill="#545454"
        stroke="none"
      >
        <path
          d="M680 1350 c0 -27 -9 -50 -20 -50 -11 0 -20 -18 -20 -41 0 -23 -14
-69 -32 -103 -25 -47 -27 -72 -10 -109 17 -37 15 -47 -7 -47 -32 0 -61 -36
-43 -54 6 -7 26 1 44 16 51 42 172 35 212 -12 19 -22 35 -29 35 -16 1 14 -11
36 -26 50 -20 18 -22 37 -8 68 14 31 10 60 -13 104 -18 34 -32 80 -32 102 0
22 -9 45 -20 52 -11 7 -20 30 -20 51 0 22 -9 39 -20 39 -11 0 -20 -22 -20 -50z
m79 -188 c39 -94 22 -132 -59 -132 -51 0 -71 10 -76 37 -11 57 37 153 76 153
20 0 46 -25 59 -58z"
        />
        <path
          d="M193 1023 c-62 -69 -12 -183 81 -183 36 0 106 73 106 110 0 87 -129
138 -187 73z"
        />
        <path
          d="M1053 1003 c-43 -47 -42 -89 4 -130 97 -88 225 12 162 128 -26 50
-122 51 -166 2z"
        />
        <path
          d="M125 809 c-104 -73 -164 -356 -97 -458 30 -47 44 -51 185 -51 l153 0
-11 -119 c-13 -140 18 -206 79 -169 29 18 37 54 42 187 7 199 -7 221 -138 221
-97 0 -98 1 -98 63 0 35 -23 103 -51 150 -71 122 -29 109 48 -14 l63 -101 115
6 c158 8 177 86 21 86 -93 0 -96 2 -160 109 -76 124 -90 133 -151 90z"
        />
        <path
          d="M1195 815 c-18 -14 -56 -65 -83 -115 -49 -87 -54 -90 -146 -90 -158
0 -140 -78 19 -86 l115 -6 63 101 c77 123 119 136 48 14 -28 -47 -51 -115 -51
-150 0 -62 -2 -63 -96 -63 -53 0 -107 -11 -120 -24 -37 -37 -32 -344 6 -376
62 -52 90 -9 90 139 l0 141 150 0 c194 0 222 33 202 236 -21 198 -120 338
-197 279z"
        />
        <path
          d="M305 475 c11 -32 785 -37 785 -5 0 12 -149 22 -397 25 -314 5 -395 1
-388 -20z"
        />
      </g>
    </svg>
  );
}

export function ContentItemSelect(props: {
  setContentItemType: Dispatch<SetStateAction<SupportedContentItemTypes>>;
}) {
  const { sidebarStore, modalStore, organizationStore } = useStores();
  const gridType = useGridType();

  const buttons = useMemo(() => {
    const b: Array<{
      onClick: () => void | Promise<void>;
      title: string;
      icon: ReactElement;
      sortOrder: number;
    }> = [];

    if (organizationStore.schoolFeedEnabledForOrganization) {
      b.push({
        onClick: () =>
          props.setContentItemType(SupportedContentItemTypes.TOPIC),
        title: "Topic",
        icon: (
          <span>
            <TopicIcon />
          </span>
        ),
        sortOrder: 0,
      });
    } else {
      b.push({
        onClick: () => props.setContentItemType(SupportedContentItemTypes.NEWS),
        title: "News",
        icon: <FlatIcon name={"flaticon-newspaper"} />,
        sortOrder: 0,
      });
    }

    b.push({
      onClick: () => props.setContentItemType(SupportedContentItemTypes.EVENT),
      icon: <FlatIcon name={"flaticon-event"} />,
      title: "Event",
      sortOrder: 5,
    });

    if (isSchoolBlocksApp()) {
      if (gridType === GridTypes.terraced) {
        b.push({
          onClick: () =>
            props.setContentItemType(SupportedContentItemTypes.MEDIA),
          icon: <FlatIcon name={"flaticon-image"} />,
          title: "Image",
          sortOrder: 2,
        });
      }
      b.push(
        {
          onClick: async () => {
            sidebarStore.close();
            modalStore.addModal({
              id: "addOrEditAlert",
              type: "addOrEditAlert",
            });
          },
          icon: <FlatIcon name={"flaticon-bell"} />,
          title: "Message/Post",
          sortOrder: 1,
        },
        {
          onClick: () =>
            sidebarStore.setSidebar({
              view: "AddSocial",
              sbtype: "GOOGLE_DRIVE",
            }),
          icon: <FlatIcon name={"flaticon-folder"} />,
          title: "File or Folder",
          sortOrder: 3,
        },
        {
          onClick: () =>
            sidebarStore.setSidebar({
              view: "AddSocial",
              sbtype: "ICAL",
            }),
          icon: <FlatIcon name={"flaticon-calendar"} />,
          title: "Calendar",
          sortOrder: 4,
        },
      );
    }

    return b;
  }, []);

  return (
    <div className={styles.container}>
      <p>Select the type of content you&#39;d like to add.</p>
      <ul>
        {buttons
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((b) => (
            <li key={b.title}>
              <button onClick={b.onClick}>
                {b.icon}
                <span>{b.title}</span>
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
}
